// =============================================================================
// features/sales/components/CampaignMembershipCard.tsx — Card "Campanha" do
// perfil do lead: em qual campanha o lead está, status do membro, tentativas,
// último desfecho, próxima ação (F38-S06, doc
// planejamento-livechat-contexto-comercial.md §3-B).
//
// Compartilhado: vive em `features/sales` (não em `features/crm`) porque o
// F38-S04 monta o MESMO componente no live chat (§3-B do plano, "o mesmo
// componente de bloco do livechat"). Exportado no barrel
// `features/sales/index.ts`.
//
// Presentational puro — não busca dados nem resolve permissão sozinho
// (mesmo padrão de `NextActionBadge.tsx`): quem monta o card
// (`CrmDetailPage.tsx`, depois `ContactPanel` no F38-S04) já resolveu o gate
// via `useCampaignMembershipGate()` e o `useLeadCommercialContext(leadId)`
// (hooks/sales/useLeadCommercialContext.ts) — o card só renderiza o bloco
// `campaign` do agregador. `campaign === null` (sem campanha ativa OU bloco
// indisponível para o ator) renderiza `null`, sem card vazio.
//
// `claimedByUserId` (schema compartilhado) é `users.id`, não `agents.id`
// (lição da memória "leads.agent_id é agents.id, não users.id" — aqui o
// vínculo já É `users.id`, mas o endpoint não resolve nome: só compara com o
// ator atual (`currentUserId`) para personalizar "por você" vs "por outro
// atendente" — nenhum novo request de usuários é disparado por este card.
// =============================================================================

import type {
  AttemptOrigin,
  CampaignMemberStatus,
  CommercialContextCampaign,
} from '@elemento/shared-schemas';
import { SALES_OUTCOME_META } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type { BadgeVariant } from '../../../components/ui/Badge';
import { Badge } from '../../../components/ui/Badge';
import { formatDateTimeOrgTz } from '../../../hooks/sales/orgTimezone';

// ---------------------------------------------------------------------------
// Rótulos pt-BR do status do membro na fila (`campaign_members.status`).
// ---------------------------------------------------------------------------

const MEMBER_STATUS_LABELS: Record<CampaignMemberStatus, string> = {
  pending: 'Aguardando início',
  available: 'Disponível para contato',
  claimed: 'Em atendimento',
  waiting_callback: 'Aguardando retorno',
  completed: 'Concluído',
  blocked: 'Bloqueado',
  removed: 'Removido da campanha',
};

const MEMBER_STATUS_BADGE_VARIANT: Record<CampaignMemberStatus, BadgeVariant> = {
  pending: 'neutral',
  available: 'info',
  claimed: 'warning',
  waiting_callback: 'warning',
  completed: 'success',
  blocked: 'danger',
  removed: 'neutral',
};

// ---------------------------------------------------------------------------
// Rótulos pt-BR da origem de tentativa — agrupamento simplificado
// campanha/agenda/direta (plano §3-B: "origem/campanha"). Reusado por
// `ContactAttemptsHistory.tsx` (mesmo agrupamento, cópia local — arquivos
// distintos deste slot, `campaign_queue`/`appointment`/demais).
// ---------------------------------------------------------------------------

export function describeAttemptOrigin(origin: AttemptOrigin): string {
  if (origin === 'campaign_queue') return 'Campanha';
  if (origin === 'appointment') return 'Agenda';
  return 'Direta';
}

// ---------------------------------------------------------------------------
// Derivação pura — testável sem renderizar React (mesmo padrão de
// `describeAttempt` em ContactAttemptsHistory.tsx).
// ---------------------------------------------------------------------------

export interface CampaignMembershipDescription {
  statusLabel: string;
  badgeVariant: BadgeVariant;
  /** null exceto quando `memberStatus === 'claimed'` e há um `claimedByUserId`. */
  claimedLabel: string | null;
  lastOutcomeLabel: string | null;
}

export function describeCampaignMembership(
  campaign: CommercialContextCampaign,
  currentUserId: string | null,
): CampaignMembershipDescription {
  const statusLabel = MEMBER_STATUS_LABELS[campaign.memberStatus];
  const badgeVariant = MEMBER_STATUS_BADGE_VARIANT[campaign.memberStatus];

  let claimedLabel: string | null = null;
  if (campaign.memberStatus === 'claimed' && campaign.claimedByUserId) {
    claimedLabel =
      campaign.claimedByUserId === currentUserId
        ? 'Em atendimento por você'
        : 'Em atendimento por outro atendente';
  }

  const lastOutcomeLabel = campaign.lastOutcomeCode
    ? (SALES_OUTCOME_META[campaign.lastOutcomeCode]?.label ?? campaign.lastOutcomeCode)
    : null;

  return { statusLabel, badgeVariant, claimedLabel, lastOutcomeLabel };
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function CampaignMembershipSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5 animate-pulse"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="h-3 w-24 rounded-xs mb-3" style={{ background: 'var(--surface-muted)' }} />
      <div className="h-4 w-full rounded-xs mb-2" style={{ background: 'var(--surface-muted)' }} />
      <div className="h-4 w-2/3 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export interface CampaignMembershipCardProps {
  /** Bloco `campaign` do agregador `commercial-context`. null = sem campanha ativa ou bloco indisponível. */
  campaign: CommercialContextCampaign | null;
  /** true enquanto o contexto comercial ainda está carregando — mostra skeleton em vez de nada. */
  isLoading?: boolean;
  /** `users.id` do ator atual — personaliza "em atendimento por você". */
  currentUserId?: string | null;
  /** Libera o link "Operar" (`sales_campaigns:operate`, resolvido pelo chamador). */
  canOperate?: boolean;
}

/**
 * Card "Campanha" — em qual campanha o lead está, status do membro na fila,
 * tentativas, último desfecho e próxima ação (`next_action_at`, fuso da
 * organização). Sem campanha ativa (ou bloco indisponível para o ator),
 * renderiza `null` — nenhum card vazio.
 */
export function CampaignMembershipCard({
  campaign,
  isLoading = false,
  currentUserId = null,
  canOperate = false,
}: CampaignMembershipCardProps): React.JSX.Element | null {
  if (isLoading) return <CampaignMembershipSkeleton />;
  if (!campaign) return null;

  const { statusLabel, badgeVariant, claimedLabel, lastOutcomeLabel } = describeCampaignMembership(
    campaign,
    currentUserId,
  );

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2
          className="font-sans font-bold text-ink-3 uppercase"
          style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
        >
          Campanha
        </h2>
        <Badge variant={badgeVariant}>{statusLabel}</Badge>
      </div>

      <p className="font-display font-bold text-ink mb-3" style={{ fontSize: 'var(--text-base)' }}>
        {campaign.name}
      </p>

      {claimedLabel && <p className="font-sans text-xs text-ink-3 mb-3">{claimedLabel}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p
            className="font-sans font-semibold text-ink-3 uppercase mb-1"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Tentativas
          </p>
          <p className="font-mono text-sm text-ink-2">{campaign.attemptCount}</p>
        </div>
        <div>
          <p
            className="font-sans font-semibold text-ink-3 uppercase mb-1"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Último desfecho
          </p>
          <p className="font-sans text-sm text-ink-2">{lastOutcomeLabel ?? 'Sem desfecho ainda'}</p>
        </div>
        {campaign.memberStatus === 'waiting_callback' && campaign.nextActionAt && (
          <div className="sm:col-span-2">
            <p
              className="font-sans font-semibold text-ink-3 uppercase mb-1"
              style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
            >
              Retorno combinado
            </p>
            <p className="font-mono text-sm text-ink-2">
              {formatDateTimeOrgTz(campaign.nextActionAt)}
            </p>
          </div>
        )}
      </div>

      {canOperate && (
        <Link
          to={`/campanhas/${campaign.id}/operar`}
          className="mt-4 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-azul hover:text-azul-deep transition-colors"
        >
          Operar
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            className="w-3.5 h-3.5"
          >
            <path d="M6 4l4 4-4 4" />
          </svg>
        </Link>
      )}
    </div>
  );
}
